import React, { useEffect, useState } from 'react';
import { Alert, Empty, Skeleton, Tag, Timeline, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fetchIssueHistory, type IssueHistoryEvent } from '../../config/api';

dayjs.extend(relativeTime);

const { Text } = Typography;

// Human labels for the audited fields. Server emits raw column names.
// status_id says "Issue status", not bare "Status" — it's easy to mistake for
// the separate Work Tasks lifecycle otherwise (see STATUS_TOOLTIP below).
const FIELD_LABELS: Record<string, string> = {
  ticket_class_id: 'Class',
  action_id: 'Action',
  root_cause_id: 'Root cause',
  comment: 'Comment',
  status_id: 'Issue status',
};

// status_id is set purely from a hardcoded per-classification mapping
// (TRIAGE_ACTIONS[].statusId in types/triage.ts — every action sets
// "Waiting for Action" except Firmware, which sets "In Progress" because an
// OTA push is already underway automatically). It is NOT derived from any
// real task/work-progress state, so it can read as confusing/arbitrary next
// to the separate Work Tasks status shown elsewhere on this issue (reported
// 2026-07-12). This tooltip only shows on status_id rows.
const STATUS_TOOLTIP =
  'Reflects the remediation implied by the confirmed classification, not real-time task progress — e.g. "Waiting for Action" means a human task is needed, "In Progress" means an automated fix (like a firmware OTA push) is already underway. See Work Tasks for actual task status.';

// Where the change came from → chip color. status-history is the status
// transition log; the rest are field-level edits.
// `color` is an antd Tag preset (accepts 'geekblue' etc.); `dot` is the
// Timeline dot color, which only treats blue/red/green/gray as presets and
// passes anything else through as raw CSS — so it must be a CSS-valid color.
const SOURCE_META: Record<string, { color: string; dot: string; label: string }> = {
  triage: { color: 'blue', dot: 'blue', label: 'triage' },
  reclassify: { color: 'geekblue', dot: 'blue', label: 're-classify' },
  'reclassify-all': { color: 'purple', dot: 'purple', label: 'bulk re-class' },
  'status-history': { color: 'gold', dot: 'gold', label: 'status' },
};

// A single field going from one value to another. `—` for a null side.
const ValueDelta: React.FC<{ from: string | null; to: string | null }> = ({ from, to }) => (
  <>
    <Text delete={from != null} type={from == null ? 'secondary' : undefined}>
      {from ?? '—'}
    </Text>
    <Text type="secondary"> → </Text>
    <Text strong>{to ?? '—'}</Text>
  </>
);

// Admin-only "who did what" timeline for one issue. Self-contained: fetches
// on mount, which — because IssueDetailPane only renders a section's children
// when it's expanded — means the request fires lazily on first open.
const TriageHistory: React.FC<{ issueId: number }> = ({ issueId }) => {
  const [events, setEvents] = useState<IssueHistoryEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setEvents(null);
    setError(false);
    fetchIssueHistory(issueId)
      .then((rows) => {
        if (alive) setEvents(rows);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [issueId]);

  if (error) {
    return <Alert type="error" showIcon message="Couldn't load triage history." style={{ margin: 8 }} />;
  }
  if (events == null) {
    return <Skeleton active paragraph={{ rows: 3 }} style={{ padding: 12 }} />;
  }
  if (events.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No recorded changes yet"
        style={{ padding: 12 }}
      />
    );
  }

  return (
    <Timeline
      style={{ padding: '12px 8px 0 8px' }}
      items={events.map((e) => {
        const src = SOURCE_META[e.source] ?? { color: 'default', label: e.source };
        const fieldLabel = FIELD_LABELS[e.field] ?? e.field;
        return {
          color: src.color === 'default' ? 'gray' : src.color,
          children: (
            <div style={{ lineHeight: 1.5 }}>
              <div>
                <Text strong>{e.changed_by ?? 'system'}</Text>
                <Text type="secondary"> · {fieldLabel} </Text>
                {e.field === 'status_id' && (
                  <Tooltip title={STATUS_TOOLTIP}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 11, marginInlineEnd: 4 }} />
                  </Tooltip>
                )}
                <Tag color={src.color} style={{ marginInlineStart: 4 }}>
                  {src.label}
                </Tag>
              </div>
              <div>
                <ValueDelta from={e.old_display} to={e.new_display} />
              </div>
              <Tooltip title={dayjs(e.at).format('YYYY-MM-DD HH:mm:ss')}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(e.at).fromNow()}
                </Text>
              </Tooltip>
            </div>
          ),
        };
      })}
    />
  );
};

export default TriageHistory;
